// How a wallet's PnL status reads on the token page's Holders and Top traders
// (plan GLOBAL-WALLET-PORTFOLIO-20261025 §3/§4, lanes L6a/L6b). The positions
// readers (L3/L4) send `pnl_status` and `pnl_reasons`; this turns them into
// one of three looks and the plain words behind them:
//
//   complete / partial    the figure alone, as a plain figure (operator,
//                         2026-10-26: no "≈", no "estimated" — figures read
//                         exact, as on Axiom); a partial figure's reasons stay
//                         on the cell (desktop tooltip, mobile sheet)
//   pending               the figure alone too (operator, 2026-10-26: no
//                         "updating" mark, no "still being counted" words); a
//                         pending row with no figure reads "—" "Not counted yet."
//   unknown / no_trades / quote_asset / unavailable
//                         "—" and the reason on hover — never a 0 that reads as
//                         "broke even". unknown means the COST is unknown:
//                         realized is shown as the server sent it (nothing
//                         sold = a measured $0.00, the figure it ranks by);
//                         "—" with why only when it sent none. no_trades the
//                         same: realized 0 is sent only as a known zero
//                         (the whole life on the tape); unrealized "—"
//
// No status on the row (today's reads) → null: the caller keeps its own path,
// so a response without the new fields renders exactly as before.
//
// The words follow the column: a reason that only touches unrealized (no
// price) is neither said nor marked on realized, and the lead names what is
// actually missing (cost, price, or a check), not always "cost".

import type { PnLReason, PnLStatus, TopTrader } from '../api/pairs'
import { formatChainAge } from './chainTime'
import { formatCompact, formatCount } from './format'
import type { HolderRowWithPnL } from './journalHolder'

export type PnlSurface = 'holders' | 'top'

/** Which figure a view is for; absent = the row as a whole. */
export type PnlColumn = 'realized' | 'unrealized' | 'total'

export type PnlView = {
  /** Show "—" instead of the figure. */
  withhold: boolean
  /** No figure carries a mark any more ("≈" and "updating" are gone: plain
      figures, operator 2026-10-26); kept so the shape stays stable. */
  marker: null
  /** The tooltip; undefined when the figure needs no words. */
  title: string | undefined
  /** The words for a "—" this status leaves without others (pending). */
  absentText?: string
  /** The words that lead a "—" of this figure, whatever else is said
      (unknown realized: why the server sent none). */
  absentLead?: string
}

/** Statuses whose figures must not be shown: the wire may carry 0 for them.
    unknown withholds the cost-based figures only — realized as the server
    sent it (pnlView, holderStatusCells). */
export const WITHHELD_STATUSES: ReadonlySet<string> = new Set([
  'unknown',
  'no_trades',
  'quote_asset',
  'unavailable',
])

/** pnl_reasons[] in plain words (backend/pkg/positions/types.go vocabulary). */
export const REASON_TEXT: Readonly<Record<string, string>> = {
  pre_coverage: 'tokens held from before our trade history starts — cost unknown',
  unobserved_acquisition: 'tokens arrived without a trade we saw — cost unknown',
  transfer_in: 'tokens received by transfer — cost unknown',
  unpriced_trade: 'some trades have no dollar price',
  token_fee_unknown: "the launchpad's token fee is unknown — cost may be understated",
  decimals_unknown: "the token's decimals are unknown — those trades are not priced",
  raw_inexact: 'some amounts are rounded',
  quantity_deficit:
    'the wallet holds fewer tokens than its trades leave — tokens left without a trade, so realized PnL may be overstated',
  quantity_unverified: 'balance not checked against the chain',
  // quiet (QUIET_EVERYWHERE): the journal is the balance source, shown as exact
  quantity_journal: 'balance from our journal',
  transfer_out: 'tokens sent out by transfer',
  moves_unchecked: 'transfers in and out were not checked',
  // the readers' read-time reasons (backend/pkg/api/pairs/positions_read.go)
  projector_not_final: "positions can't yet be certified complete",
  fold_queued: "this wallet's newest trades are waiting to be counted",
  projector_stale: 'position figures are running behind and may be out of date',
  projector_unpublished: 'position figures are not available yet',
  positions_unavailable: 'position figures could not be read right now',
  no_indexed_trades: 'no position counted for this wallet yet',
  chain_unavailable: 'the chain balance could not be read — our own figure is shown instead',
  mark_unavailable: 'no priced trade in its main pool, or the price read failed',
  observation_untimed: 'the balance has no change time, so it was not checked against trades',
  token_decimals_unknown: "the token's decimals are unknown — its current value is not priced",
  basis_unknown: 'no token here was ever bought at a known price — PnL cannot be worked out',
  // unrealized (Top traders: and total) only — realized of the known units stands
  held_basis_unknown:
    'none of the tokens held now has a known cost — unrealized PnL cannot be worked out',
  balance_changed_after_tape:
    'the balance changed after the last fully indexed trades — newer trades may not be counted yet',
  tape_certified_behind:
    'trade indexing is running behind; the newest trades may not be counted yet',
  // quiet (QUIET_EVERYWHERE): never said on a PnL figure
  quote_decimals_unknown: "the quote asset's decimals are unknown — quote amounts are withheld",
  // Avg MC only (avgMcapCell), never on PnL
  mcap_missing: 'market cap was not recorded for these buys',
  // on a "—" in Bought / Sold / Realized only (HISTORY_UNPROVEN_TEXT)
  history_unproven: "our trade history may not cover this token's full life",
}

/** The words for a code this build does not know: neutral, never the raw code. */
export const UNKNOWN_REASON_TEXT = 'another check on this figure is incomplete'

/** A reason in words; a code this build does not know reads as a neutral line. */
export function reasonText(code: PnLReason): string {
  return REASON_TEXT[code] ?? UNKNOWN_REASON_TEXT
}

// Tokens whose cost was never seen: the "none came from a trade we saw" lead.
const ACQUISITION_REASONS: ReadonlySet<string> = new Set([
  'pre_coverage',
  'unobserved_acquisition',
  'transfer_in',
])

// Reasons that leave part of the COST unknown (the "part of the cost" lead).
const COST_REASONS: ReadonlySet<string> = new Set([
  ...ACQUISITION_REASONS,
  'unpriced_trade',
  'token_fee_unknown',
  'decimals_unknown',
  'basis_unknown',
  'held_basis_unknown',
])

// Reasons about the value unrealized uses: they say nothing about realized.
// token_decimals_unknown on Holders = no decimals to value the mark;
// held_basis_unknown = the tokens held have no known cost (realized stands).
const UNREALIZED_ONLY: ReadonlySet<string> = new Set([
  'mark_unavailable',
  'token_decimals_unknown',
  'held_basis_unknown',
])

// Reasons that inform but do not, alone, make a figure "≈".
const INFO_ONLY: ReadonlySet<string> = new Set(['tape_certified_behind'])

// Never said on a PnL figure: raw_inexact is non-blocking for the verdict;
// quote_decimals_unknown touches only quote amounts the UI does not show.
// mcap_missing is about the Avg MC column (avgMcapCell), not PnL.
// history_unproven says why a figure is ABSENT ("—"), never on a shown one,
// and does not change the row's status (pnlCell / activityCell).
// quantity_journal / quantity_unverified: the journal is the source of token
// balances and its rows are shown as exact (operator, 2026-11-02: no hedge
// labels, ever) — where a balance came from is never said on a figure.
const QUIET_EVERYWHERE: ReadonlySet<string> = new Set([
  'raw_inexact',
  'quote_decimals_unknown',
  'mcap_missing',
  'history_unproven',
  'quantity_journal',
  'quantity_unverified',
])

// On Top traders every row carries quantity_unverified (no balance is read):
// the scope note says so once; a marker on every row would say nothing.
// token_decimals_unknown there meant the QUOTE's decimals (an older reader,
// now quote_decimals_unknown): bought/sold quote amounts only, not PnL.
const QUIET_ON_TOP: ReadonlySet<string> = new Set(['quantity_unverified', 'token_decimals_unknown'])

const PARTIAL_LEAD = 'Part of the cost is unknown: PnL counts only the tokens with a known cost.'
const DEFICIT_LEAD =
  'Part of this position is not confirmed: the wallet holds fewer tokens on chain than its trades show.'
const NO_MARK_LEAD = 'No current price: unrealized PnL cannot be valued.'
const NO_VALUE_LEAD = 'Unrealized PnL cannot be valued right now.'
const PARTIAL_CHECK_LEAD = 'Part of this figure could not be checked.'
// a pending row with no figure: neutral, no "updating"
const NOT_COUNTED_TEXT = 'Not counted yet.'
const UNKNOWN_LEAD = 'Cost unknown: none of these tokens came from a trade we saw.'
const BASIS_UNKNOWN_LEAD = 'Cost unknown: no token here was ever bought at a known price.'
const COST_UNKNOWN_LEAD = 'Cost unknown for this wallet.'
const UNKNOWN_OTHER_LEAD = 'PnL unknown for this wallet.'
const WITHHELD_TEXT: Readonly<Record<string, string>> = {
  no_trades: 'No indexed trades of this token for this wallet.',
  quote_asset: 'TON or a dollar stablecoin: it prices other tokens, so it has no PnL.',
  unavailable: 'PnL is unavailable right now.',
}
// a reason that only restates its status's own words
const RESTATES: Readonly<Record<string, string>> = {
  no_trades: 'no_indexed_trades',
  unknown: 'basis_unknown',
}

/** The codes worth saying on this surface and column. */
function relevantCodes(
  reasons: readonly PnLReason[],
  surface: PnlSurface,
  column: PnlColumn | undefined
): PnLReason[] {
  return reasons.filter(
    (code) =>
      !QUIET_EVERYWHERE.has(code) &&
      !(surface === 'top' && QUIET_ON_TOP.has(code)) &&
      !(column === 'realized' && UNREALIZED_ONLY.has(code))
  )
}

function reasonLines(codes: readonly PnLReason[]): string[] {
  const out: string[] = []
  for (const code of codes) {
    const line = `• ${reasonText(code)}`
    if (!out.includes(line)) out.push(line)
  }
  return out
}

/** The unknown lead: which cost is missing, or none named. "None came from
    a trade we saw" only when the reasons say exactly that — a Top traders
    row has Bought/Sold beside it. */
function unknownLead(codes: readonly PnLReason[]): string {
  if (codes.includes('basis_unknown')) return BASIS_UNKNOWN_LEAD
  if (codes.some((c) => ACQUISITION_REASONS.has(c))) return UNKNOWN_LEAD
  if (codes.some((c) => COST_REASONS.has(c))) return COST_UNKNOWN_LEAD
  return UNKNOWN_OTHER_LEAD
}

/** The partial lead for what is actually missing. */
function partialLead(codes: readonly PnLReason[], proceeds: string | null): string {
  if (proceeds != null || codes.some((c) => COST_REASONS.has(c))) return PARTIAL_LEAD
  if (codes.includes('quantity_deficit')) return DEFICIT_LEAD
  if (codes.includes('mark_unavailable')) return NO_MARK_LEAD
  if (codes.includes('token_decimals_unknown')) return NO_VALUE_LEAD
  return PARTIAL_CHECK_LEAD
}

function proceedsLine(usd: number | null | undefined): string | null {
  if (typeof usd !== 'number' || !Number.isFinite(usd) || usd <= 0) return null
  return `$${formatCompact(usd)} of sales had no known cost and are not counted as profit.`
}

export const joinLines = (lines: ReadonlyArray<string | null | undefined>): string | undefined => {
  const kept = lines.filter((l): l is string => typeof l === 'string' && l !== '')
  return kept.length > 0 ? kept.join('\n') : undefined
}

/**
 * The look of one wallet's PnL. null when the row carries no status — the
 * caller then renders as it did before the positions readers.
 */
export function pnlView(args: {
  status?: PnLStatus | null
  reasons?: readonly PnLReason[] | null
  proceedsWithoutBasisUsd?: number | null
  surface: PnlSurface
  /** The figure this view is for; absent = every reason counts. */
  column?: PnlColumn
}): PnlView | null {
  const { status, surface } = args
  if (status == null || status === '') return null
  const codes = relevantCodes(args.reasons ?? [], surface, args.column)
  const reasons = reasonLines(codes)
  const proceeds = proceedsLine(args.proceedsWithoutBasisUsd)
  switch (status) {
    case 'complete':
      return { withhold: false, marker: null, title: undefined }
    case 'pending':
      // a plain figure, no "updating" words; no figure → "Not counted yet."
      return { withhold: false, marker: null, title: undefined, absentText: NOT_COUNTED_TEXT }
    case 'partial': {
      // A plain figure, no mark. On Top traders a row is "partial" for the
      // unverified balance alone, and realized is whole when only the price
      // is missing: words only for something this figure really lacks (not
      // a note alone).
      const says = codes.some((c) => !INFO_ONLY.has(c)) || proceeds != null
      return {
        withhold: false,
        marker: null,
        title: says ? joinLines([partialLead(codes, proceeds), ...reasons, proceeds]) : undefined,
      }
    }
    case 'unknown': {
      // basis_unknown wins over pending (the readers send both): the lead
      // says so once, the pending reasons follow
      const said = reasonLines(codes.filter((c) => c !== RESTATES.unknown))
      const title = joinLines([unknownLead(codes), ...said, proceeds])
      // Realized is not cost-unknown when nothing was sold: the server sends
      // the measured figure ($0.00 included) and ranks by it — shown as sent.
      // Sent none (a sale of unknown cost, no position, history unproven): "—"
      // and why. Unrealized and total need the cost: always "—".
      if (args.column === 'realized') {
        return { withhold: false, marker: null, title: undefined, absentLead: title }
      }
      return { withhold: true, marker: null, title }
    }
    case 'no_trades': {
      // Realized as the server sent it: it sends 0 only when the tape holds
      // the token's whole life (known zeros); otherwise none — "—" and why.
      // Unrealized / total: no position, always "—".
      const view = withheldView(status, codes)
      if (args.column !== 'realized') return view
      return { withhold: false, marker: null, title: undefined, absentLead: view.title }
    }
    default:
      return withheldView(status, codes)
  }
}

// no_trades / quote_asset / unavailable: "—" and the status's words, with the
// reasons the reader gave (not for a cash-like token: it has no PnL at all).
function withheldView(status: string, codes: readonly PnLReason[]): PnlView {
  const text = WITHHELD_TEXT[status]
  if (text == null) {
    // a code this build does not know: the figure as sent, no claim about it
    return { withhold: false, marker: null, title: undefined }
  }
  if (status === 'quote_asset') return { withhold: true, marker: null, title: text }
  const said = reasonLines(codes.filter((c) => c !== RESTATES[status]))
  return { withhold: true, marker: null, title: joinLines([text, ...said]) }
}

/**
 * `mark_ts` as unix seconds. The readers send seconds (like last_active_ts);
 * an older response sent an RFC3339 string, which is still read. Anything
 * else — or no time at all — is null.
 */
export function parseMarkTs(v: number | string | null | undefined): number | null {
  if (typeof v === 'number') return Number.isFinite(v) && v > 0 ? v : null
  if (typeof v !== 'string' || v.trim() === '') return null
  const ms = Date.parse(v)
  return Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : null
}

/** The words for an old price under unrealized PnL; null when the price is fresh. */
export function staleMarkText(
  markTs: number | string | null | undefined,
  stale: boolean | null | undefined,
  nowMs: number
): string | null {
  if (stale !== true) return null
  const ts = parseMarkTs(markTs)
  const age = ts != null ? ` from ${formatChainAge(ts, nowMs)} ago` : ''
  return `Unrealized uses the price${age}: the token's main pool has not traded since.`
}

const signedUsdText = (v: number): string =>
  v < 0 ? `−$${formatCompact(-v)}` : `$${formatCompact(v)}`

const finiteNum = (v: number | null | undefined): v is number =>
  typeof v === 'number' && Number.isFinite(v)

/** A Top trader's lifetime figures in this pool (L4), one line; null without them. */
export function lifetimeText(trader: {
  lifetime_realized_pnl_usd?: number | null
  lifetime_bought_usd?: number | null
  lifetime_sold_usd?: number | null
  lifetime_trades?: number | null
}): string | null {
  const parts: string[] = []
  if (finiteNum(trader.lifetime_realized_pnl_usd)) {
    parts.push(`realized ${signedUsdText(trader.lifetime_realized_pnl_usd)}`)
  }
  if (finiteNum(trader.lifetime_bought_usd)) {
    parts.push(`bought $${formatCompact(trader.lifetime_bought_usd)}`)
  }
  if (finiteNum(trader.lifetime_sold_usd)) {
    parts.push(`sold $${formatCompact(trader.lifetime_sold_usd)}`)
  }
  // no win rate: a row never states one (the operator, 2026-11-03)
  if (finiteNum(trader.lifetime_trades)) parts.push(`${formatCount(trader.lifetime_trades)} trades`)
  return parts.length > 0 ? `All time in this pool: ${parts.join(' · ')}` : null
}

// ---- the cell model: one pure function per figure, desktop and mobile only
// render it (value, mark, words, withheld) ----

const DASH = '—'

/** One PnL figure as both platforms show it. */
export type PnlCell = {
  /** The figure, unsigned (the tone carries the direction), or "—". */
  text: string
  /** always null: plain figures (kept for a stable shape) */
  marker: null
  tone: 'up' | 'down' | undefined
  /** Every word behind the figure, one source for the tooltip / sheet. */
  info: string | undefined
  /** The status withholds the figure ("—" whatever the wire carried). */
  withheld: boolean
}

// PnL without a sign: the colour carries the direction (feedback 2026-08-27).
const unsignedUsdText = (v: number | null | undefined): string =>
  v != null && Number.isFinite(v) ? `$${formatCompact(Math.abs(v))}` : DASH

const toneOfValue = (v: number | null | undefined): 'up' | 'down' | undefined => {
  if (v == null || !Number.isFinite(v) || v === 0) return undefined
  return v > 0 ? 'up' : 'down'
}

const HISTORY_UNPROVEN_TEXT =
  "Our trade history may not cover this token's full life, so this isn't a measured zero."

/** history_unproven: the words for an absent Bought / Sold / Realized ("—"
    where a zero would be, because no zero was measured). Status unchanged. */
export function historyNote(reasons: readonly PnLReason[] | null | undefined): string | null {
  return (reasons ?? []).includes('history_unproven') ? HISTORY_UNPROVEN_TEXT : null
}

const DECIMALS_HIDDEN_TEXT = "Token decimals are unknown, so this amount can't be valued yet."
const HIDDEN_FIGURE_TEXT = "This figure isn't available yet."

/**
 * The words for a hidden figure ("—") that its status and reasons do not
 * explain: a quiet code is quiet only while the figure is shown — a bare
 * "—" with nothing behind it is never left.
 */
export function hiddenFigureText(
  reasons: readonly PnLReason[] | null | undefined,
  fallback: string = HIDDEN_FIGURE_TEXT
): string {
  return (reasons ?? []).includes('token_decimals_unknown') ? DECIMALS_HIDDEN_TEXT : fallback
}

/**
 * A figure under its status. No view and no note: the figure as it always
 * was (an older read, a missing figure stays a bare "—" as before).
 * Withheld: "—" and the reason, never a 0 that reads as "broke even".
 * Hidden under a status with nothing else to say: hiddenFigureText.
 */
export function pnlCell(
  v: number | null | undefined,
  view: PnlView | null,
  note: string | null = null,
  reasons: readonly PnLReason[] | null = null,
  /** words for this figure only when it is absent (historyNote) */
  absentNote: string | null = null
): PnlCell {
  if (view?.withhold) {
    return { text: DASH, marker: null, tone: undefined, info: view.title, withheld: true }
  }
  const text = unsignedUsdText(v)
  if (text === DASH && view?.absentLead != null) {
    // a figure the status allows but the server did not send (unknown
    // realized): "—" and why, as withheld — no second line under it
    const why = joinLines([view.absentLead, absentNote])
    return { text, marker: null, tone: undefined, info: why, withheld: true }
  }
  const info = joinLines([view?.title, note])
  if (text === DASH) {
    const said = joinLines([info, absentNote])
    const why = said ?? (view != null ? hiddenFigureText(reasons, view.absentText) : undefined)
    return { text, marker: null, tone: undefined, info: why, withheld: false }
  }
  return { text, marker: null, tone: toneOfValue(v), info, withheld: false }
}

/** A Top trader's Remaining hidden for an unknown balance (no balance_base
    on a positions-read row): the words for its "—". An older read: none. */
export function traderRemainingInfo(t: TopTrader): string | undefined {
  if (t.pnl_status == null || t.pnl_status === '') return undefined
  if (typeof t.balance_base === 'number' && Number.isFinite(t.balance_base)) return undefined
  return hiddenFigureText(t.pnl_reasons, t.pnl_status === 'pending' ? NOT_COUNTED_TEXT : undefined)
}

/** Realized, Unrealized and Remaining of one holder row. */
export function holderStatusCells(h: HolderRowWithPnL, nowMs: number) {
  const p = h.pnl
  const view = (column: PnlColumn) => (p ? pnlView({ ...p, surface: 'holders', column }) : null)
  const stale = p ? staleMarkText(p.markTs, p.markStale, nowMs) : null
  const unrealized = pnlCell(h.unrealizedUsd, view('unrealized'), stale, p?.reasons)
  return {
    realized: pnlCell(h.realizedUsd, view('realized'), null, p?.reasons, historyNote(p?.reasons)),
    unrealized,
    /** unrealized is valued at an old price: said on hover, never as a mark
        under the figure (the operator, 2026-11-03: no such marks) */
    stale: stale != null && unrealized.text !== DASH,
    /** Remaining is a plain figure: where the balance came from is never said
        (operator, 2026-11-02 — journal rows are exact, no hedge labels).
        Kept in the shape for the callers that still read it. */
    remainInfo: undefined as string | undefined,
    remainSays: false,
  }
}

/** PnL % of a Top trader: of the money it bought in this pool. Not shown for
    a withheld figure, nor when total mixes scopes (pool realized + token
    position unrealized) — over this pool's buys it would mean nothing. */
function traderPnlPct(t: TopTrader, withheld: boolean, mixedTotal: boolean): number | null {
  if (withheld || mixedTotal) return null
  if (t.total_pnl_usd == null || !((t.bought_usd ?? 0) > 0)) return null
  return (t.total_pnl_usd / (t.bought_usd as number)) * 100
}

/**
 * PnL and Realized ⇄ Unrealized of one Top trader. `hasStatus` false = the
 * older read (no pnl_status): the caller keeps its own path for PnL. The
 * Realized words: the status, the caller's lead, then the pool's all-time figures — or, in Unrealized mode, an old price.
 */
export function topTraderCells(
  t: TopTrader,
  opts: { unreal: boolean; nowMs: number; mixedTotal: boolean; realizedLead?: string | null }
) {
  const view = (column: PnlColumn) =>
    pnlView({
      status: t.pnl_status,
      reasons: t.pnl_reasons,
      proceedsWithoutBasisUsd: t.proceeds_without_basis_usd,
      surface: 'top',
      column,
    })
  const total = view('total')
  const pnl = pnlCell(t.total_pnl_usd, total, null, t.pnl_reasons)
  const stale = opts.unreal ? staleMarkText(t.mark_ts, t.mark_stale, opts.nowMs) : null
  const realized = pnlCell(
    opts.unreal ? t.unrealized_pnl_usd : t.realized_pnl_usd,
    view(opts.unreal ? 'unrealized' : 'realized'),
    joinLines([opts.realizedLead, opts.unreal ? stale : lifetimeText(t)]) ?? null,
    t.pnl_reasons,
    opts.unreal ? null : historyNote(t.pnl_reasons)
  )
  return {
    hasStatus: total != null,
    pnl,
    pnlPct: traderPnlPct(t, pnl.withheld, opts.mixedTotal),
    realized,
    /** in Unrealized mode, valued at an old price: said on hover only */
    stale: stale != null && realized.text !== DASH,
  }
}

// ---- plain figures (operator, 2026-10-26): a measured zero is "$0.00" /
// "0", not "—" and not coloured; only an absent figure is "—" ----

/** Money that can be a real zero: absent "—", 0 "$0.00". */
export function moneyText(v: number | null | undefined): string {
  return v != null && Number.isFinite(v) ? `$${formatCompact(v)}` : DASH
}

/** A token amount that can be a real zero: absent → null (no line), 0 → "0". */
export function qtyText(v: number | null | undefined): string | null {
  if (v == null || !Number.isFinite(v)) return null
  return v === 0 ? '0' : formatCompact(v)
}

/** The bought/sold colour, for a non-zero figure only. */
export function activityTone<T extends 'up' | 'down'>(v: number | null | undefined, tone: T) {
  return v != null && Number.isFinite(v) && v !== 0 ? tone : undefined
}

/** One Bought / Sold figure: money over its token amount. */
export type ActivityCell = {
  text: string
  /** the token amount under the money; null = no second line */
  sub: string | null
  tone: 'up' | 'down' | undefined
  /** why the money is "—" when trades exist but carry no dollar price */
  info: string | undefined
}

/**
 * Bought / Sold. A measured zero is "$0.00" over "0". Dollars 0 over a
 * non-zero token amount is not a zero — those trades had no dollar price:
 * "—" with the words (and the unpriced-trade count when the read sends
 * one). No dollars at all: the token amount, else "—".
 */
export function activityCell(args: {
  side: 'buy' | 'sell'
  usd: number | null | undefined
  base: number | null | undefined
  unpricedTrades?: number | null
  /** history_unproven: an absent figure here is not a measured zero */
  reasons?: readonly PnLReason[] | null
}): ActivityCell {
  const { usd, base } = args
  const tone = args.side === 'buy' ? 'up' : 'down'
  const qty = qtyText(base)
  const hasUsd = usd != null && Number.isFinite(usd)
  if (hasUsd && usd === 0 && base != null && Number.isFinite(base) && base > 0) {
    const what = args.side === 'buy' ? 'Buys were not priced' : 'Sells were not priced'
    const n = args.unpricedTrades
    const count =
      typeof n === 'number' && Number.isFinite(n) && n > 0
        ? ` — ${formatCount(n)} trade${n === 1 ? '' : 's'} had no dollar price`
        : ''
    return { text: DASH, sub: qty, tone: undefined, info: `${what}${count}.` }
  }
  if (hasUsd)
    return { text: moneyText(usd), sub: qty, tone: activityTone(usd, tone), info: undefined }
  if (qty != null) return { text: qty, sub: null, tone: activityTone(base, tone), info: undefined }
  return { text: DASH, sub: null, tone: undefined, info: historyNote(args.reasons) ?? undefined }
}

const NO_BUYS_TEXT = 'No buys'
const MCAP_MISSING_TEXT = 'Market cap was not recorded for these buys'

/**
 * Avg MC (average market cap of the wallet's buys): the figure, or "—" with
 * why — no buys at all, or buys whose market cap was not recorded
 * (mcap_missing). `buys` 0 = a measured "no buys"; null = not known.
 */
export function avgMcapCell(args: {
  avgUsd: number | null | undefined
  buys: number | null | undefined
  reasons?: readonly PnLReason[] | null
}): { text: string; info: string | undefined } {
  if (args.avgUsd != null && Number.isFinite(args.avgUsd)) {
    return { text: moneyText(args.avgUsd), info: undefined }
  }
  if (args.buys === 0) return { text: DASH, info: NO_BUYS_TEXT }
  if ((args.reasons ?? []).includes('mcap_missing')) return { text: DASH, info: MCAP_MISSING_TEXT }
  return { text: DASH, info: undefined }
}

/** Whether a holder bought at all, as Avg MC needs it: 0 = a measured "no
    buys" (the token amount bought is 0 — dollars alone can be 0 for unpriced
    buys), 1 = bought, null = not known. */
export function holderBuys(h: { boughtUsd: number | null; boughtBase: number | null }) {
  if (h.boughtBase != null) return h.boughtBase === 0 ? 0 : 1
  if (h.boughtUsd != null) return h.boughtUsd === 0 ? 0 : 1
  return null
}

/**
 * Sort two values with the missing ones (withheld or never sent) LAST in
 * both directions. Equal values → the address decides (as before). Both
 * missing → 0: the sort is stable, so they keep the server's order (the
 * census by balance) — a column missing on every row is not alphabetical.
 */
export function compareNullsLast(
  a: number | null,
  b: number | null,
  dir: 1 | -1,
  addrA: string,
  addrB: string
): number {
  const am = a == null || Number.isNaN(a)
  const bm = b == null || Number.isNaN(b)
  if (am !== bm) return am ? 1 : -1
  if (am && bm) return 0
  if (a !== b) return ((a as number) - (b as number)) * dir
  if (addrA === addrB) return 0
  return addrA < addrB ? -1 : 1
}
